/**
 * DemoLabLanding - Rich landing experience for the Lab page.
 *
 * Showcases PropAlgos capabilities with clearly-labeled demo data:
 * - Featured strategy with equity curve
 * - Next steps for new users
 * - Monthly heatmap
 *
 * NOTE: This surface must never render fabricated user-specific data
 * (rankings, competition standings, prize pools). See
 * docs/features/arena_competitions_rebuild.md for the history and the
 * rebuild plan for real competition/leaderboard widgets.
 *
 * All interactive elements prompt users to spin up compute.
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FiZap,
  FiTrendingUp,
  FiCode,
  FiAward,
  FiDatabase,
  FiArrowRight,
  FiPlay,
  FiEdit3,
  FiCpu,
} from 'react-icons/fi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

import ComputePromptModal from '../common/ComputePromptModal';
import { STRATEGY_TEMPLATES } from './StrategyTemplatePicker';
import { MonthlyHeatmap } from '../tearsheet/TearSheet';
import {
  MOMENTUM_ALPHA,
  DEMO_STRATEGIES,
} from '../../data/demoData';

interface DemoLabLandingProps {
  username?: string;
  activeSessions?: number;
  runningCost?: number;
  /**
   * When true, suppress the sticky status bar and the "Welcome back" header.
   * Use this when DemoLabLanding is rendered as a "demo data" strip beneath
   * a parent surface that already provides those greetings (e.g., the
   * new-user branch of LabPage).
   */
  embedded?: boolean;
}

export default function DemoLabLanding({
  username = 'there',
  activeSessions = 0,
  runningCost = 0,
  embedded = false,
}: DemoLabLandingProps) {
  const [showComputeModal, setShowComputeModal] = useState(false);
  const [computeAction, setComputeAction] = useState<'edit' | 'backtest' | 'analyze' | 'create'>('analyze');
  const [preselectedTemplatePath, setPreselectedTemplatePath] = useState<string | undefined>();

  const featuredStrategy = MOMENTUM_ALPHA;
  const tearSheet = featuredStrategy.tearSheet;

  // Prepare equity curve data
  const equityData = useMemo(() => {
    if (!tearSheet.returns) return [];

    let cumulative = 1;
    let benchCumulative = 1;
    const start = new Date(tearSheet.metadata.start_date);

    return tearSheet.returns.map((ret, i) => {
      cumulative *= (1 + ret);
      if (tearSheet.benchmark_returns && tearSheet.benchmark_returns[i] !== undefined) {
        benchCumulative *= (1 + tearSheet.benchmark_returns[i]);
      }

      const date = new Date(start);
      date.setDate(date.getDate() + i);

      return {
        date: date.toISOString().split('T')[0],
        strategy: cumulative,
        benchmark: tearSheet.benchmark_returns ? benchCumulative : undefined,
      };
    });
  }, [tearSheet]);

  const openComputeModal = (action: 'edit' | 'backtest' | 'analyze' | 'create', templatePath?: string) => {
    setComputeAction(action);
    setPreselectedTemplatePath(templatePath);
    setShowComputeModal(true);
  };

  const cryptoTemplate = STRATEGY_TEMPLATES.find(t => t.id === 'crypto-rsi');

  // Map strategy category → template path for "Use as Template" flow
  const categoryToTemplatePath: Record<string, string | undefined> = {
    momentum: STRATEGY_TEMPLATES.find(t => t.id === 'momentum')?.path,
    mean_reversion: STRATEGY_TEMPLATES.find(t => t.id === 'mean-reversion')?.path,
    factor: STRATEGY_TEMPLATES.find(t => t.id === 'momentum')?.path,
    ml: STRATEGY_TEMPLATES.find(t => t.id === 'ml-classifier')?.path,
    crypto: STRATEGY_TEMPLATES.find(t => t.id === 'crypto-rsi')?.path,
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-gray-800 border border-gray-700 rounded p-2 shadow-lg text-xs">
        <p className="text-gray-400 mb-1">{label}</p>
        {payload.map((entry: any, i: number) => (
          <p key={i} style={{ color: entry.color }}>
            {entry.name === 'strategy' ? 'Strategy' : entry.name === 'benchmark' ? 'S&P 500' : entry.name}:
            <span className="ml-1 font-medium tabular-nums">
              {((entry.value - 1) * 100).toFixed(1)}%
            </span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-full bg-black">
      {/* Top Status Bar — suppressed when embedded under a parent surface */}
      {!embedded && (
        <div className="sticky top-0 z-10 bg-gray-950/95 backdrop-blur border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${activeSessions > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-600'}`} />
                  <span className="text-gray-400">
                    Active Sessions: <span className="text-white font-medium">{activeSessions}</span>
                  </span>
                </div>
                <div className="text-gray-400">
                  Running Cost: <span className="text-white font-medium">${runningCost.toFixed(2)}/hr</span>
                </div>
              </div>

              <button
                onClick={() => openComputeModal('create')}
                className="flex items-center gap-2 px-4 py-1.5 bg-profit text-white rounded-lg text-sm font-medium hover:bg-profit/90 transition-colors"
              >
                <FiZap className="w-4 h-4" />
                Launch Compute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Welcome Header — suppressed when embedded under a parent greeting */}
        {!embedded && (
          <div className="mb-2">
            <h1 className="text-2xl font-semibold text-white">
              Welcome back, {username}
            </h1>
            <p className="text-gray-400 mt-1">
              Explore demo strategies or spin up compute to build your own
            </p>
          </div>
        )}

        {/* Featured Crypto Notebook Banner */}
        {cryptoTemplate && (
          <section className="relative bg-gradient-to-r from-gray-900 via-cyan-950/30 to-gray-900 border border-cyan-800/30 rounded-xl p-5 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-full bg-gradient-to-l from-cyan-500/5 to-transparent pointer-events-none" />
            <div className="flex items-center justify-between relative">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-cyan-500/15 flex items-center justify-center flex-shrink-0">
                  <FiTrendingUp className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-lg font-semibold text-white">Crypto RSI Scanner</h2>
                    <span className="px-2 py-0.5 bg-cyan-900/50 text-cyan-400 text-xs rounded-full">New</span>
                  </div>
                  <p className="text-sm text-gray-400">
                    Scan 200+ crypto pairs across 8 timeframes — with interactive live dashboard
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                      GPU-Accelerated
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                      Crypto
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                      Live Dashboard
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => openComputeModal('create', cryptoTemplate.path)}
                className="flex items-center gap-2 px-5 py-2.5 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-500 transition-colors shadow-lg shadow-cyan-500/20 flex-shrink-0"
              >
                <FiZap className="w-4 h-4" />
                Try It Now
              </button>
            </div>
          </section>
        )}

        {/* Featured Strategy */}
        <section className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-profit/20 flex items-center justify-center">
                  <FiTrendingUp className="w-5 h-5 text-profit" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-white">{featuredStrategy.name}</h2>
                    <span className="px-2 py-0.5 bg-green-900/50 text-green-400 text-xs rounded-full">
                      Featured
                    </span>
                  </div>
                  <p className="text-sm text-gray-400">{featuredStrategy.description}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Link
                  to={`/dashboard/strategies/${featuredStrategy.id}`}
                  className="px-3 py-1.5 bg-gray-800 text-gray-300 rounded-lg text-sm hover:bg-gray-700 transition-colors"
                >
                  View Full Tearsheet
                </Link>
                <button
                  onClick={() => openComputeModal('create', categoryToTemplatePath[featuredStrategy.category])}
                  className="flex items-center gap-2 px-3 py-1.5 bg-profit/20 text-profit rounded-lg text-sm hover:bg-profit/30 transition-colors"
                >
                  <FiEdit3 className="w-4 h-4" />
                  Edit
                </button>
              </div>
            </div>
          </div>

          {/* Equity Curve */}
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-0.5 bg-profit"></div>
                  <span className="text-gray-400">Strategy</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-0.5 bg-gray-500"></div>
                  <span className="text-gray-400">S&P 500</span>
                </div>
              </div>
              <span className="text-xs text-gray-500">Log scale</span>
            </div>

            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={equityData} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#6B7280"
                  tick={{ fill: '#6B7280', fontSize: 10 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getFullYear().toString().slice(2)}`;
                  }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  stroke="#6B7280"
                  tick={{ fill: '#6B7280', fontSize: 10 }}
                  tickFormatter={(v) => `${((v - 1) * 100).toFixed(0)}%`}
                  scale="log"
                  domain={['auto', 'auto']}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={1} stroke="#6B7280" strokeDasharray="3 3" />
                <Line
                  type="monotone"
                  dataKey="benchmark"
                  stroke="#6B7280"
                  strokeWidth={1}
                  dot={false}
                  name="benchmark"
                />
                <Line
                  type="monotone"
                  dataKey="strategy"
                  stroke="#005AB5"
                  strokeWidth={2}
                  dot={false}
                  name="strategy"
                />
              </LineChart>
            </ResponsiveContainer>

            {/* Metrics Row */}
            <div className="mt-4 grid grid-cols-4 gap-4">
              <MetricCard label="CAGR" value={`${(tearSheet.basic.cagr * 100).toFixed(0)}%`} positive />
              <MetricCard label="Sharpe" value={tearSheet.basic.sharpe_ratio.toFixed(2)} positive />
              <MetricCard label="Max DD" value={`${(tearSheet.basic.max_drawdown * 100).toFixed(0)}%`} />
              <MetricCard label="Win Rate" value={`${(tearSheet.basic.win_rate * 100).toFixed(0)}%`} positive />
            </div>
          </div>
        </section>

        {/* Next Steps */}
        <section className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
          <h3 className="text-lg font-semibold text-white mb-4">Your Next Steps</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <ActionCard
              icon={<FiCode className="w-5 h-5" />}
              title="Create Strategy"
              description="AI-assisted workflow"
              onClick={() => openComputeModal('create')}
            />
            <ActionCard
              icon={<FiPlay className="w-5 h-5" />}
              title="Run Backtest"
              description="Full forensic analysis"
              onClick={() => openComputeModal('backtest')}
            />
            <ActionCard
              icon={<FiAward className="w-5 h-5" />}
              title="Competitions"
              description="Browse open competitions"
              href="/dashboard/competitions"
            />
            <ActionCard
              icon={<FiDatabase className="w-5 h-5" />}
              title="Explore Data"
              description="Market datasets ready"
              href="/dashboard/data"
            />
          </div>
        </section>

        {/* Monthly Heatmap */}
        <div
          className="cursor-pointer"
          onClick={() => openComputeModal('analyze')}
        >
          <MonthlyHeatmap monthlyReturns={tearSheet.monthly_returns} />
          <p className="mt-2 text-xs text-gray-500 text-center">
            Click to drill into trade details (requires compute)
          </p>
        </div>

        {/* More Demo Strategies */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Explore More Strategies</h3>
            <Link
              to="/dashboard/strategies"
              className="text-sm text-profit hover:underline flex items-center gap-1"
            >
              View all
              <FiArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {DEMO_STRATEGIES.slice(1).map((strategy) => (
              <StrategyCard
                key={strategy.id}
                strategy={strategy}
                onEdit={() => openComputeModal('create', categoryToTemplatePath[strategy.category])}
              />
            ))}
          </div>
        </section>
      </div>

      {/* Compute Modal */}
      <ComputePromptModal
        isOpen={showComputeModal}
        onClose={() => setShowComputeModal(false)}
        action={computeAction}
        initialTemplatePath={preselectedTemplatePath}
      />
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function MetricCard({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  return (
    <div className="bg-gray-800/50 rounded-lg p-3 text-center">
      <p className={`text-2xl font-bold tabular-nums ${positive ? 'text-profit' : 'text-white'}`}>
        {value}
      </p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
}

interface ActionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick?: () => void;
  href?: string;
  highlight?: boolean;
}

function ActionCard({ icon, title, description, onClick, href, highlight }: ActionCardProps) {
  const content = (
    <div className={`h-full p-4 rounded-lg border transition-all cursor-pointer ${
      highlight
        ? 'border-amber-700/50 bg-amber-900/20 hover:bg-amber-900/30'
        : 'border-gray-800 bg-gray-800/50 hover:bg-gray-800'
    }`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
        highlight ? 'bg-amber-900/50 text-amber-400' : 'bg-gray-700 text-gray-300'
      }`}>
        {icon}
      </div>
      <h4 className="font-medium text-white">{title}</h4>
      <p className="text-sm text-gray-400 mt-1">{description}</p>
    </div>
  );

  if (href) {
    return <Link to={href}>{content}</Link>;
  }

  return <div onClick={onClick}>{content}</div>;
}

interface StrategyCardProps {
  strategy: typeof DEMO_STRATEGIES[0];
  onEdit: () => void;
}

function StrategyCard({ strategy, onEdit }: StrategyCardProps) {
  const isWarning = strategy.id === 'demo-ml-overfit';

  return (
    <div className={`bg-gray-900/50 border rounded-xl p-5 ${
      isWarning ? 'border-amber-800/50' : 'border-gray-800'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-medium text-white">{strategy.name}</h4>
          <p className="text-sm text-gray-400 mt-1 line-clamp-2">{strategy.description}</p>
        </div>
        {isWarning && (
          <span className="px-2 py-0.5 bg-amber-900/50 text-amber-400 text-xs rounded">
            Cautionary
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center">
          <p className={`text-lg font-bold tabular-nums ${
            strategy.tearSheet.basic.cagr > 0.1 ? 'text-profit' : 'text-gray-400'
          }`}>
            {(strategy.tearSheet.basic.cagr * 100).toFixed(0)}%
          </p>
          <p className="text-xs text-gray-500">CAGR</p>
        </div>
        <div className="text-center">
          <p className={`text-lg font-bold tabular-nums ${
            strategy.tearSheet.basic.sharpe_ratio > 1 ? 'text-profit' : 'text-gray-400'
          }`}>
            {strategy.tearSheet.basic.sharpe_ratio.toFixed(2)}
          </p>
          <p className="text-xs text-gray-500">Sharpe</p>
        </div>
        <div className="text-center">
          <p className={`text-lg font-bold tabular-nums ${
            strategy.tearSheet.advanced.deflated_sharpe_ratio > 0.5 ? 'text-profit' : 'text-loss'
          }`}>
            {strategy.tearSheet.advanced.deflated_sharpe_ratio.toFixed(2)}
          </p>
          <p className="text-xs text-gray-500">DSR</p>
        </div>
      </div>

      {strategy.highlight && (
        <p className="text-xs text-gray-500 mb-3 italic">{strategy.highlight}</p>
      )}

      <div className="flex gap-2">
        <Link
          to={`/dashboard/strategies/${strategy.id}`}
          className="flex-1 text-center px-3 py-1.5 bg-gray-800 text-gray-300 rounded-lg text-sm hover:bg-gray-700 transition-colors"
        >
          Details
        </Link>
        <button
          onClick={onEdit}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-profit/20 text-profit rounded-lg text-sm hover:bg-profit/30 transition-colors"
        >
          <FiCpu className="w-3.5 h-3.5" />
          Use as Template
        </button>
      </div>
    </div>
  );
}
